'use client';

import { useEffect, useState } from 'react';
import { Check, Play, Volume2 } from 'lucide-react';

const voices = [
  {
    name: 'Sarah',
    accent: 'US English',
    avatar: 'S',
    gradient: 'from-pink-500 to-rose-500'
  },
  {
    name: 'James',
    accent: 'UK English',
    avatar: 'J',
    gradient: 'from-blue-500 to-indigo-500'
  },
  {
    name: 'Maria',
    accent: 'Spanish',
    avatar: 'M',
    gradient: 'from-orange-500 to-amber-500'
  },
  {
    name: 'Yuki',
    accent: 'Japanese',
    avatar: 'Y',
    gradient: 'from-purple-500 to-violet-500'
  }
];

export default function VoiceListMockup() {
  const [selected, setSelected] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [waveHeights, setWaveHeights] = useState<number[]>(Array(12).fill(0.3));

  useEffect(() => {
    let selectionInterval: NodeJS.Timeout | null = null;
    let playTimeout: NodeJS.Timeout | null = null;
    let isMounted = true;

    selectionInterval = setInterval(() => {
      if (!isMounted) return;
      if (playTimeout) clearTimeout(playTimeout);
      setSelected((s) => (s + 1) % voices.length);
      setIsPlaying(true);
      playTimeout = setTimeout(() => {
        if (isMounted) setIsPlaying(false);
      }, 1800);
    }, 2800);

    return () => {
      isMounted = false;
      if (selectionInterval) clearInterval(selectionInterval);
      if (playTimeout) clearTimeout(playTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setWaveHeights(Array(12).fill(0.3));
      return;
    }

    let waveInterval: NodeJS.Timeout | null = null;
    waveInterval = setInterval(() => {
      setWaveHeights(
        Array(12)
          .fill(0)
          .map(() => 0.2 + Math.random() * 0.8)
      );
    }, 80);

    return () => {
      if (waveInterval) clearInterval(waveInterval);
    };
  }, [isPlaying]);

  return (
    <div className="w-full h-full bg-white dark:bg-slate-900 rounded-2xl border border-border/50 overflow-hidden shadow-xl">
      <div className="px-4 py-3 bg-gradient-to-r from-purple-500/5 to-pink-500/5 border-b border-border/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
            <Volume2 className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-foreground">
            Voice Library
          </span>
        </div>
        <span className="text-xs text-primary font-semibold bg-primary/10 px-2.5 py-1 rounded-full">
          100+ voices
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Voice cards grid */}
        <div className="grid grid-cols-2 gap-2">
          {voices.map((voice, i) => (
            <div
              key={i}
              className={`relative p-3 rounded-xl border transition-all duration-300 cursor-pointer ${
                i === selected
                  ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                  : 'border-border/50 bg-slate-50 dark:bg-slate-800/50 hover:border-primary/30'
              }`}
            >
              {/* Selection indicator */}
              {i === selected && (
                <div className="absolute top-2 right-2">
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <div
                  className={`w-9 h-9 rounded-full bg-gradient-to-br ${voice.gradient} flex items-center justify-center shadow-sm`}
                >
                  <span className="text-white text-sm font-bold">
                    {voice.avatar}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {voice.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {voice.accent}
                  </p>
                </div>
              </div>

              {/* Waveform area */}
              <div className="h-7 flex items-center justify-center gap-[3px] bg-muted/30 rounded-lg px-2">
                {i === selected && isPlaying ? (
                  waveHeights.map((h, j) => (
                    <div
                      key={j}
                      className="w-1 bg-gradient-to-t from-primary to-emerald-400 rounded-full transition-all duration-75"
                      style={{ height: `${h * 100}%` }}
                    />
                  ))
                ) : (
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Play className="w-3 h-3" />
                    <span className="text-xs">Preview</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
